import React, { useState } from "react";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  KeyRound,
  User as UserIcon,
  ShieldCheck,
  Download,
  Trash2,
  Check,
  X,
  Copy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function AccountPage() {
  const { user, changePassword, refresh, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  // Change password
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  const submitPwd = async (e) => {
    e.preventDefault();
    if (next !== confirmPwd) {
      toast.error(t("account.mismatch"));
      return;
    }
    setPwdBusy(true);
    try {
      await changePassword(current, next);
      toast.success(t("account.success"));
      setCurrent(""); setNext(""); setConfirmPwd("");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setPwdBusy(false);
    }
  };

  if (!user || user === false) return null;

  return (
    <main data-testid="account-page" className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-10">
        <p className="label-small text-[#8B3A3A] mb-3">{t("account.eyebrow")}</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">{t("account.heading")}</h1>
        <p className="text-[#4A4238] mt-3 max-w-2xl">{t("account.intro")}</p>
      </header>

      <section className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-10 mb-10">
        <p className="label-small text-[#8B3A3A] mb-5 flex items-center gap-2"><UserIcon size={14} /> {t("account.identity_heading")}</p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8">
          <div>
            <dt className="label-small mb-1">{t("account.label_name")}</dt>
            <dd className="font-display text-2xl text-[#1A1918]" data-testid="account-name">{user.name}</dd>
          </div>
          <div>
            <dt className="label-small mb-1">{t("account.label_email")}</dt>
            <dd className="text-[#1A1918] break-all" data-testid="account-email">{user.email}</dd>
          </div>
          <div>
            <dt className="label-small mb-1">{t("account.label_grade")}</dt>
            <dd>
              <span className="grade-pill" data-testid="account-grade">{user.grade}</span>
              <p className="text-xs italic text-[#7A7061] mt-2">{t("account.grade_note")}</p>
            </dd>
          </div>
        </dl>
      </section>

      <section className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-10 mb-10">
        <p className="label-small text-[#8B3A3A] mb-5 flex items-center gap-2"><KeyRound size={14} /> {t("account.change_pwd_heading")}</p>
        <form onSubmit={submitPwd} className="space-y-6" data-testid="account-pwd-form">
          <div>
            <label className="label-small block mb-2">{t("account.label_current")}</label>
            <input data-testid="account-current-pwd" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="input-etched" autoComplete="current-password" />
          </div>
          <div>
            <label className="label-small block mb-2">{t("account.label_new")}</label>
            <input data-testid="account-new-pwd" type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} className="input-etched" autoComplete="new-password" />
          </div>
          <div>
            <label className="label-small block mb-2">{t("account.label_confirm")}</label>
            <input data-testid="account-confirm-pwd" type="password" required minLength={6} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="input-etched" autoComplete="new-password" />
          </div>
          <div className="flex justify-end">
            <button data-testid="account-submit-btn" type="submit" disabled={pwdBusy} className="btn-primary">
              {pwdBusy ? t("account.submitting") : t("account.submit")}
            </button>
          </div>
        </form>
      </section>

      <TwoFactorSection user={user} onChange={refresh} />

      <DataRightsSection userIsAdmin={user.role === "admin"} onDeleted={async () => { await logout(); navigate("/"); }} />
    </main>
  );
}

// ---------------- Two-Factor Section ----------------
function TwoFactorSection({ user, onChange }) {
  const { t } = useI18n();
  const isOn = !!user.mfa_enabled;
  const [setup, setSetup] = useState(null);   // {secret, otpauth_uri, qr_png_base64}
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelSetup = () => {
    setSetup(null); setPassword(""); setCode("");
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { password, code: code.trim() });
      setBackupCodes(data.backup_codes);
      setSetup(null); setPassword(""); setCode("");
      toast.success(t("account.mfa_enabled_toast"));
      await onChange();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd });
      toast.success(t("account.mfa_disabled_toast"));
      setConfirmDisable(false); setDisablePwd("");
      await onChange();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const copyBackup = async () => {
    if (!backupCodes) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      toast.success(t("account.mfa_backup_copied"));
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <section data-testid="account-mfa-section" className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-10 mb-10">
      <p className="label-small text-[#8B3A3A] mb-5 flex items-center gap-2"><ShieldCheck size={14} /> {t("account.mfa_heading")}</p>

      {backupCodes && (
        <div className="mb-6 border border-[#B8860B] bg-[#F4EEDF] p-6" data-testid="account-mfa-backup-codes">
          <p className="label-small text-[#B8860B] mb-3">{t("account.mfa_backup_title")}</p>
          <p className="text-sm text-[#4A4238] mb-4">{t("account.mfa_backup_intro")}</p>
          <ol className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-[#1A1918] mb-4">
            {backupCodes.map((c) => (<li key={c}>{c}</li>))}
          </ol>
          <div className="flex gap-2">
            <button type="button" onClick={copyBackup} className="btn-secondary inline-flex items-center gap-2" data-testid="account-mfa-backup-copy">
              <Copy size={12} /> {t("account.mfa_backup_copy")}
            </button>
            <button type="button" onClick={() => setBackupCodes(null)} className="btn-ghost inline-flex items-center gap-2" data-testid="account-mfa-backup-dismiss">
              <Check size={12} /> {t("account.mfa_backup_dismiss")}
            </button>
          </div>
        </div>
      )}

      {!isOn && !setup && (
        <div>
          <p className="text-sm text-[#4A4238] mb-4 max-w-2xl">{t("account.mfa_off_body")}</p>
          <button type="button" onClick={startSetup} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="account-mfa-start-btn">
            <ShieldCheck size={12} /> {busy ? t("account.mfa_setting_up") : t("account.mfa_start_btn")}
          </button>
        </div>
      )}

      {!isOn && setup && (
        <form onSubmit={enable} className="space-y-6" data-testid="account-mfa-setup-form">
          <p className="text-sm text-[#4A4238] max-w-2xl">{t("account.mfa_setup_intro")}</p>
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="border border-[#C1B49A] bg-white p-3 inline-block shrink-0">
              <img
                src={`data:image/png;base64,${setup.qr_png_base64}`}
                alt="TOTP QR"
                width={192}
                height={192}
                data-testid="account-mfa-qr"
                className="block"
              />
            </div>
            <div className="text-sm text-[#4A4238] flex-1">
              <p className="mb-2">{t("account.mfa_manual_label")}</p>
              <p className="font-mono text-xs bg-[#F4EEDF] border border-[#C1B49A] p-3 select-all break-all" data-testid="account-mfa-secret">
                {setup.secret}
              </p>
            </div>
          </div>
          <div>
            <label className="label-small block mb-2">{t("account.mfa_password_label")}</label>
            <input data-testid="account-mfa-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-etched" />
          </div>
          <div>
            <label className="label-small block mb-2">{t("account.mfa_code_label")}</label>
            <input data-testid="account-mfa-code" type="text" inputMode="numeric" autoComplete="one-time-code" required minLength={6} maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} className="input-etched font-mono tracking-[0.3em]" placeholder="123 456" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancelSetup} className="btn-secondary inline-flex items-center gap-2" data-testid="account-mfa-cancel-btn">
              <X size={12} /> {t("account.mfa_cancel")}
            </button>
            <button type="submit" disabled={busy || !password || code.trim().length < 6} className="btn-primary inline-flex items-center gap-2" data-testid="account-mfa-enable-btn">
              <Check size={12} /> {busy ? t("account.mfa_verifying") : t("account.mfa_enable_btn")}
            </button>
          </div>
        </form>
      )}

      {isOn && !confirmDisable && (
        <div>
          <p className="text-sm text-[#1A1918] mb-3 flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#B8860B]" />
            <span className="font-medium">{t("account.mfa_is_on")}</span>
          </p>
          <p className="text-sm text-[#4A4238] mb-4 max-w-2xl">{t("account.mfa_on_body")}</p>
          <button type="button" onClick={() => setConfirmDisable(true)} className="btn-ghost inline-flex items-center gap-2 text-[#8B3A3A]" data-testid="account-mfa-disable-start-btn">
            <X size={12} /> {t("account.mfa_disable_btn")}
          </button>
        </div>
      )}

      {isOn && confirmDisable && (
        <form onSubmit={disable} className="space-y-4" data-testid="account-mfa-disable-form">
          <p className="text-sm text-[#4A4238]">{t("account.mfa_disable_confirm")}</p>
          <div>
            <label className="label-small block mb-2">{t("account.mfa_password_label")}</label>
            <input data-testid="account-mfa-disable-password" type="password" required value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} className="input-etched" autoComplete="current-password" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setConfirmDisable(false); setDisablePwd(""); }} className="btn-secondary inline-flex items-center gap-2">
              <X size={12} /> {t("account.mfa_cancel")}
            </button>
            <button type="submit" disabled={busy || !disablePwd} className="btn-primary inline-flex items-center gap-2" data-testid="account-mfa-disable-confirm-btn">
              <Check size={12} /> {busy ? t("account.mfa_disabling") : t("account.mfa_disable_confirm_btn")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------------- Data Rights Section (AVG / GDPR) ----------------
function DataRightsSection({ userIsAdmin, onDeleted }) {
  const { t } = useI18n();
  const [exportBusy, setExportBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const doExport = async () => {
    setExportBusy(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nlp-magick-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("account.data_exported_toast"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setExportBusy(false);
    }
  };

  const doDelete = async (e) => {
    e.preventDefault();
    setDeleteBusy(true);
    try {
      await api.post("/me/delete", { password: deletePwd });
      toast.success(t("account.deleted_toast"));
      await onDeleted();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <section data-testid="account-data-rights-section" className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-10">
      <p className="label-small text-[#8B3A3A] mb-5 flex items-center gap-2"><Download size={14} /> {t("account.data_rights_heading")}</p>
      <p className="text-sm text-[#4A4238] mb-6 max-w-2xl">{t("account.data_rights_intro")}</p>

      <div className="border-t border-[#C1B49A] pt-6 mb-8">
        <p className="font-display text-xl text-[#1A1918] mb-2">{t("account.data_export_title")}</p>
        <p className="text-sm text-[#4A4238] mb-4 max-w-2xl">{t("account.data_export_body")}</p>
        <button type="button" onClick={doExport} disabled={exportBusy} className="btn-secondary inline-flex items-center gap-2" data-testid="account-export-btn">
          <Download size={12} /> {exportBusy ? t("account.data_exporting") : t("account.data_export_btn")}
        </button>
      </div>

      <div className="border-t border-[#C1B49A] pt-6">
        <p className="font-display text-xl text-[#8B3A3A] mb-2">{t("account.delete_title")}</p>
        <p className="text-sm text-[#4A4238] mb-4 max-w-2xl">{t("account.delete_body")}</p>
        {userIsAdmin && (
          <p className="text-xs italic text-[#7A7061] mb-4" data-testid="account-delete-admin-notice">
            {t("account.delete_admin_blocked")}
          </p>
        )}
        {!userIsAdmin && !confirmDelete && (
          <button type="button" onClick={() => setConfirmDelete(true)} className="btn-ghost inline-flex items-center gap-2 text-[#8B3A3A]" data-testid="account-delete-start-btn">
            <Trash2 size={12} /> {t("account.delete_start_btn")}
          </button>
        )}
        {!userIsAdmin && confirmDelete && (
          <form onSubmit={doDelete} className="space-y-4 mt-4 border border-[#8B3A3A]/40 p-5 bg-[#F4EEDF]" data-testid="account-delete-form">
            <p className="text-sm text-[#1A1918] font-medium">{t("account.delete_final_warning")}</p>
            <div>
              <label className="label-small block mb-2">{t("account.mfa_password_label")}</label>
              <input data-testid="account-delete-password" type="password" required value={deletePwd} onChange={(e) => setDeletePwd(e.target.value)} className="input-etched" autoComplete="current-password" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setConfirmDelete(false); setDeletePwd(""); }} className="btn-secondary inline-flex items-center gap-2">
                <X size={12} /> {t("account.mfa_cancel")}
              </button>
              <button type="submit" disabled={deleteBusy || !deletePwd} className="btn-primary inline-flex items-center gap-2" data-testid="account-delete-confirm-btn">
                <Trash2 size={12} /> {deleteBusy ? t("account.deleting") : t("account.delete_confirm_btn")}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
